import { Alert, Badge, Button, Card, Group, Stack, Text, TextInput, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useState } from "react";

import { errorMessage, type Session } from "@/api";
import { type Column, DataTable, DateUTC, PageHeader, QueryState } from "@/components";

import { useSession } from "../session";
import { tokenStore } from "../token";
import { ChangePasswordForm } from "./ChangePasswordForm";
import { useSessionsQuery, useTerminateSessionMutation, useUpdateMeMutation } from "./api";

/** /account (spec §3.4). Deliberately no 2FA disable control. */
export function AccountPage() {
  const { me, refreshMe, logout } = useSession();
  const [name, setName] = useState(me?.display_name ?? "");
  const updateMe = useUpdateMeMutation();
  const sessions = useSessionsQuery();
  const terminate = useTerminateSessionMutation();
  const current = tokenStore.get();

  const saveName = async () => {
    try {
      await updateMe.mutateAsync({ display_name: name.trim() });
      await refreshMe();
      notifications.show({ color: "green", message: "Display name saved." });
    } catch (e) {
      notifications.show({ color: "red", message: errorMessage(e) });
    }
  };

  const signOut = async (s: Session) => {
    if (!s.token) return;
    if (s.token === current) return logout();
    try {
      await terminate.mutateAsync(s.token);
      notifications.show({ color: "green", message: "That device was signed out." });
    } catch (e) {
      notifications.show({ color: "red", message: errorMessage(e) });
    }
  };

  const columns: Column<Session>[] = [
    {
      key: "device",
      header: "Device",
      render: (s) => (
        <Group gap={6} wrap="nowrap">
          <Text size="sm" lineClamp={1} maw={420} title={s.user_agent}>
            {s.user_agent || "Unknown device"}
          </Text>
          {s.token === current && (
            <Badge size="xs" color="green" style={{ flexShrink: 0 }} data-testid="current-session">
              this device
            </Badge>
          )}
        </Group>
      ),
    },
    {
      key: "ip",
      header: "IP",
      render: (s) => (
        <Text size="sm" ff="monospace">
          {s.ip || "—"}
        </Text>
      ),
    },
    {
      key: "created",
      header: "Signed in",
      sortValue: (s) => s.created_at,
      render: (s) => <DateUTC value={s.created_at} local />,
    },
    {
      key: "action",
      header: "",
      align: "right",
      render: (s) => (
        <Button
          size="compact-sm"
          variant="subtle"
          color="red"
          onClick={() => void signOut(s)}
          data-testid="session-signout"
        >
          Sign out{s.token === current ? "" : " device"}
        </Button>
      ),
    },
  ];

  return (
    <>
      <PageHeader title="Account" description={me?.email} />
      <Stack gap="lg" maw={960}>
        <Card withBorder>
          <Title order={4} mb="sm">
            Profile
          </Title>
          <Group align="flex-end">
            <TextInput
              label="Display name"
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              w={320}
              data-testid="account-name"
            />
            <Button
              variant="light"
              onClick={saveName}
              loading={updateMe.isPending}
              disabled={name.trim() === (me?.display_name ?? "")}
            >
              Save
            </Button>
          </Group>
        </Card>
        <Card withBorder>
          <Title order={4} mb="sm">
            Password
          </Title>
          <ChangePasswordForm />
        </Card>
        <Card withBorder>
          <Title order={4} mb="sm">
            Two-factor authentication
          </Title>
          <Text size="sm">
            Two-factor authentication is enabled and required for every account. It cannot be turned off.
          </Text>
          <Alert mt="sm" color="blue" title="Lost your authenticator?">
            Use one of your backup codes to sign in. If you have none left, ask{" "}
            {me?.user_type === "PORTAL" ? "your partner account owner" : "a superadmin"} to reset your two-factor
            authentication; you will enrol a new authenticator at your next sign-in.
          </Alert>
        </Card>
        <Card withBorder>
          <Title order={4} mb="sm">
            Active sessions
          </Title>
          <QueryState query={sessions}>
            {(rows) => (
              <DataTable
                testId="sessions-table"
                columns={columns}
                rows={[...rows].sort((a, b) => (b.created_at ?? "").localeCompare(a.created_at ?? ""))}
                rowKey={(s) => s.token ?? `${s.ip}-${s.created_at}`}
              />
            )}
          </QueryState>
        </Card>
      </Stack>
    </>
  );
}
